import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useEffect, useState } from "react";

import { COUNTRY_OPTIONS_NAME_ONLY } from "@/utils/selectOptions";
import NotificationAlertOptions from "@/layouts/Alerts";
import { markInvalidFields } from "@/validation/markInvalidFields";
import {
  updateClient,
  getClient,
  getClientProducts,
} from "@/services/clientService/clientActions";
import { useImageUpload } from "@/services/ImageServices/useImageUpload";
import type { PickedImage } from "../User/adding-user/ImageUploadContainer";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import withRouter from "@/utils/withRouter";
import UpdateClientView from "./UpdateClientView";
import { makeClientOnChange, showClientMessage } from "./clientFormHandlers";
import { asCaught } from "@/services/caughtError";

type UpdateClientContainerProps = {
  // injected by withRouter
  router?: { params?: { id?: string } };
};

const UpdateClientContainer = (props: UpdateClientContainerProps) => {
  const dispatch = useAppDispatch();
  const uploadImage = useImageUpload();
  const id = props.router?.params?.id; // via withRouter

  const errors = useAppSelector((s) => s.errors);
  const clientsSlice = useAppSelector((s) => s.clients);
  const permission = useCurrentPermission();
  const [clientImg, setClientImg] = useState<PickedImage>({});
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState({
    name: "",
    about: "",
    contacts: { email: "", number: "", website: "" },
    social_links: { facebook: "", linkedin: "", twitter: "" },
    adresse: "",
    country_code: "",
    legal_name: "",
  });
  const [validation, setValidation] = useState({
    name: true,
    country_code: true,
  });
  const [products, setProducts] = useState<any[]>([]); // any: copies clients.products, typed any[] there (clientSlice.ts:41)

  useEffect(() => {
    if (id) {
      dispatch(getClient(id));
      dispatch(getClientProducts(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (clientsSlice?.client) setClient(clientsSlice.client);
    if (clientsSlice?.products) setProducts(clientsSlice.products);
  }, [clientsSlice?.client, clientsSlice?.products]);

  // Server errors: stop the spinner and mark the invalid fields. Adjusting state while rendering
  // (React: "storing information from previous renders") instead of an effect that only
  // copies the store value into state (S9 mirror-effect, SCRUM-2254).
  const [seenErrors, setSeenErrors] = useState<typeof errors | null>(null);
  if (errors !== seenErrors) {
    setSeenErrors(errors);
    if (errors?.errors) {
      setValidation(markInvalidFields(validation, errors.errors));
      setLoading(false);
    }
  }

  useEffect(() => {
    showClientMessage(clientsSlice?.message, dispatch, setLoading, setClient);
  }, [clientsSlice?.message, dispatch]);

  const ImageUploadRecievedHandler = (img: PickedImage) => setClientImg(img);

  const onChange = makeClientOnChange(setClient, setValidation);

  const onChangeCountryCode = (action) => {
    setClient((prev) => ({ ...prev, country_code: action?.value || "" }));
    setValidation((prev) => ({ ...prev, country_code: true }));
  };

  const onClickUpdate = async (e) => {
    e.preventDefault();

    if (!client.country_code?.trim()) {
      setValidation((prev) => ({ ...prev, country_code: false }));
      return;
    }

    // Freshly picked file: upload first, then save inline with the returned URL
    // (useImageUpload clears images.url on success). The two direct-save branches
    // — an already-hosted secure_url, or no image — are unchanged.
    if (clientImg && clientImg.data) {
      const path = `/CLIENTS/${clientImg.imgName}`;
      setLoading(true);
      try {
        const url = await uploadImage(clientImg.data, path);
        if (id) dispatch(updateClient({ ...client, clientImg: url }, id));
      } catch (err) {
        setLoading(false);
        NotificationAlertOptions(
          "error",
          asCaught(err)?.response?.data?.message ||
            asCaught(err)?.message ||
            "Saving the client failed. Please try again."
        );
      }
      return;
    }

    if (clientImg && clientImg.secure_url !== undefined) {
      const payload = { ...client, clientImg: clientImg.secure_url };
      if (id) dispatch(updateClient(payload, id));
      return;
    }

    if (id) dispatch(updateClient(client, id));
  };

  return (
    <UpdateClientView
      client={client}
      products={products}
      updatedCountries={COUNTRY_OPTIONS_NAME_ONLY}
      validation={validation}
      permission={permission}
      loading={loading}
      onChange={onChange}
      onChangeCountryCode={onChangeCountryCode}
      onClickUpdate={onClickUpdate}
      ImageUploadRecievedHandler={ImageUploadRecievedHandler}
      clientImg={clientImg}
    />
  );
};

export default withRouter(UpdateClientContainer);
